<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import TJButton from "@components/TJButton.vue";
import Finder from "@components/Finder.vue";
import { useBookingStore } from "@stores/BookingStore";
import Spinner from "@components/Spinner.vue";

//serviceType is either vaccinations or consultations.
const bookingStore = useBookingStore();
const route = useRoute();
const serviceType = ref("vaccinations"); // Default to 'vaccinations'

const props = defineProps({
  style: {
    type: String,
    required: false,
  },
});

// two different styling for this unit.
// on homepage, bg is shown and buttons are white.
// when props.style is 'simple' bg is hidden and buttons are black.
// on mobile, buttons are always black, and without bg

const buttonClass = computed(() => {
  const baseClasses = [
    "max-md:ring-neutral-700",
    "ring-1",
    "max-md:text-neutral-700",
  ];

  const styleClasses =
    props.style === "simple"
      ? ["text-neutral-700", "ring-neutral-700"]
      : ["ring-white", "text-white"];

  // Combine all classes into a single string
  return [...baseClasses, ...styleClasses].join(" ");
});

const openTab = (tabName) => {
  serviceType.value = tabName;
  bookingStore.setServiceType(tabName);
};

onMounted(() => {
  // Set the initial service type based on the URL query parameter
  // Check if route and route.query are available, as useRoute() might return undefined
  // if the component is not rendered within a <router-view> context.
  if (route && route.query) {
    const serviceTypeFromQuery = route.query.serviceType;
    if (
      serviceTypeFromQuery &&
      (serviceTypeFromQuery === "vaccinations" ||
        serviceTypeFromQuery === "consultations")
    ) {
      serviceType.value = serviceTypeFromQuery;
    }
  }
  // Ensure the bookingStore is updated with the determined serviceType (from URL or default)
  bookingStore.setServiceType(serviceType.value);
});
</script>

<template>
  <Suspense>
    <template #default>
      <section
        class="flex items-center bg-white py-60 pt-60 text-white hero"
        :class="{ '2xl:min-h-[600px]': props.style !== 'simple' }">
        <div v-if="style !== 'simple'" class="hidden md:flex hero-background">
          <div
            class="bg-image"
            :class="{ 'bg-visible': serviceType === 'vaccinations' }"
            :style="{
              backgroundImage: `url(${require('@images/hero-vaccinations.jpg')})`,
            }"></div>
          <div
            class="bg-image"
            :class="{ 'bg-visible': serviceType === 'consultations' }"
            :style="{
              backgroundImage: `url(${require('@images/hero-consultations.jpg')})`,
            }"></div>
        </div>

        <div class="container">
          <ul class="flex max-sm:flex-col gap-4 sm:gap-6 mb-40">
            <li>
              <TJButton
                icon="ri-syringe-line"
                :class="[
                  buttonClass,
                  {
                    'opacity-60': serviceType !== 'vaccinations',
                    'ring-2 opacity-100 md:bg-zinc-300/[0.1]':
                      serviceType === 'vaccinations',
                  },
                ]"
                @click="openTab('vaccinations')">
                Private Vaccinations
              </TJButton>
            </li>
            <li>
              <TJButton
                :class="[
                  buttonClass,
                  {
                    'opacity-60': serviceType !== 'consultations',
                    'ring-2  opacity-100 md:bg-zinc-300/[0.1]':
                      serviceType === 'consultations',
                  },
                ]"
                icon="ri-earth-line"
                @click="openTab('consultations')">
                Travel Consultations
              </TJButton>
            </li>
          </ul>

          <div class="mb-40 max-md:text-grey" v-if="style !== 'simple'">
            <template v-if="serviceType === 'vaccinations'">
              <h2 class="mb-10 text-31 md:text-white lg:text-49">
                Private Vaccinations, Made Simple
              </h2>
              <p class="max-w-[500px] font-light">
                Compare prices, find nearby clinics, and book your private
                vaccine with no waiting and no hassle.
              </p>
            </template>

            <template v-if="serviceType === 'consultations'">
              <h2
                class="mb-10 text-31 max-md:text-grey md:text-white lg:text-49">
                Travel Consultations, Tailored to You
              </h2>
              <p class="max-w-[500px] font-light">
                Not sure which vaccines you need? Book a consultation with a
                local clinic. Travel smart. Stay safe.
              </p>
            </template>
          </div>

          <!-- Content Sections -->
          <Finder :type="serviceType" :style="style"></Finder>
        </div>
      </section>
    </template>

    <template #fallback>
      <Spinner minHeight="600" /><!-- Fallback content while loading -->
    </template>
  </Suspense>
</template>

<style scoped lang="css">
.hero {
  position: relative;
  z-index: 1;

  .hero-background {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    overflow: hidden;
    z-index: -1;

    &::before {
      position: absolute;
      content: "";
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      z-index: 1; /* Ensures gradient is above images but below content */
      background: linear-gradient(
        90deg,
        rgba(18, 18, 18, 0.6) 46.36%,
        rgba(69, 72, 70, 0.5) 68.84%,
        rgba(228, 228, 228, 0.4) 100%
      );
    }
  }

  .bg-image {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: top;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
    z-index: 0;

    /* Adjust background position on desktop to keep syringe visible above search box */
    @media (min-width: 768px) {
      background-position: center 35%;
    }
  }

  .bg-visible {
    opacity: 1;
  }
}
</style>
